import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Sparkles, CheckSquare, BookOpen, Target, ChevronDown, ChevronUp, Send, Plus, PlusCircle } from 'lucide-react'
import { Button } from '@lovable/components/ui/button'
import { Badge } from '@lovable/components/ui/badge'
import { Textarea } from '@lovable/components/ui/textarea'
import { Card } from '@lovable/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@lovable/components/ui/popover'
import { Skeleton } from '@lovable/components/ui/skeleton'
import { GoalCard } from '@lovable/components/GoalCard'
import { QuickActionsBar } from '@lovable/components/QuickActionsBar'
import { CreditCounter } from '@lovable/components/CreditCounter'
import { LearningGoal as ApiLearningGoal, READY_PROMPTS } from '@/pages/engine/EngineHelper'
import { Task } from '@/types/commonTypes'
import { TaskPreview } from '@/composites/TaskPreview/TaskPreview'
import { t } from 'i18next'
import { Modal } from '@/common/components/Modal/Modal'
import { LenseSelection } from './LenseSelection'
import { EngineForm } from '@/pages/engine/Engine'
import { ExerciseType } from '@/api/gameTypes'

interface LearningGoal {
  id: number
  title: string
  description: string
}
interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  subContent?: string
  noEffect?: boolean
}

interface Props {
  onUserPromt: (text: string) => void
  learningGoals: ApiLearningGoal[]
  onAddLearningGoal: (goal: ApiLearningGoal) => void
  onRemoveLearningGoal: (id: number) => void
  onUpdateLearningGoal: (goal: ApiLearningGoal) => void
  taskSlots: { [key: number]: Task }
  setShouldPoll: (shouldPoll: boolean) => void
  autoPrompt: string | null
  setAutoPrompt: (value: string | null) => void
  isPolling: boolean
  readyTasks: boolean
  is3D: boolean
  realAnswers: string[] | null | undefined
  onBackToLenses: () => void
  onContinue: () => void
  errorStatus: string | null
  onCancel: () => void
  latestSubContent: string | undefined
  setLatestSubContent: (subContent: string | undefined) => void
  onDoneWithGoals: () => void
}

export const Tasks: React.FC<Props> = ({
  learningGoals,
  onAddLearningGoal,
  onRemoveLearningGoal,
  onUpdateLearningGoal,
  taskSlots,
  onUserPromt,
  setShouldPoll,
  autoPrompt,
  //setAutoPrompt,
  isPolling,
  readyTasks,
  is3D,
  //realAnswers,
  onBackToLenses,
  onContinue,
  errorStatus,
  onCancel,
  latestSubContent,
  setLatestSubContent,
  onDoneWithGoals,
}: Props) => {
  const location = useLocation()
  const hasLenseSelected = !!location.state?.lense
  const chatMessagesEndRef = useRef<HTMLDivElement>(null)
  const [goals, setGoals] = useState<LearningGoal[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [goalsExpanded, setGoalsExpanded] = useState(true)
  const [gameStoryContent, setGameStoryContent] = useState<string>()
  const [chatMessage, setChatMessage] = useState('')
  const [showFullGame, setShowFullGame] = useState(readyTasks)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      content: readyTasks
        ? t('engine.chat.initial_message_ready_tasks', 'Hey there! 🎉 Parsing your tasks from the document...')
        : t(
            'engine.chat.initial_message',
            "Hey there! 🎉 Crafting your game's goals. Edit and refine them to suit your needs and select continue to draft the rest of your game",
          ),
    },
  ])
  const [subContentsExpanded, setSubContentsExpanded] = useState<string[]>([])
  const [isUpdatingGoals, setIsUpdatingGoals] = useState(!readyTasks)
  const [isUpdatingStory] = useState(false)
  const [isUpdatingTasks, setIsUpdatingTasks] = useState(readyTasks)
  const [tasksCollapsed, setTasksCollapsed] = useState(false)
  const [showLenseDialog, setShowLenseDialog] = useState(false)
  const [storyRemoved, setStoryRemoved] = useState(false)
  const initialGoalsStarted = useRef(false)
  const initialGoalsSDone = useRef(false)
  const initialTasksDone = useRef(false)
  const revisionPromptDone = useRef(true) //This should be false if we want the missing word and order items auto re-check enabled. Disabled for testing purposes
  const willStartPolling = useRef(false)
  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (errorStatus) {
      const errorResponse = {
        id: (Date.now() + 1).toString(),
        role: 'assistant' as const,
        content: errorStatus,
      }
      setChatMessages((prev) => prev.filter((msg) => msg.role !== 'system'))
      setChatMessages((prev) => [...prev, errorResponse])
    }
  }, [errorStatus])

  useEffect(() => {
    setTasks(
      Object.values(taskSlots)
        .slice(readyTasks ? 0 : 1)
        .map((task) => task),
    )
  }, [taskSlots, readyTasks])

  useEffect(() => {
    if (tasks.length > 0 && !readyTasks) {
      setGameStoryContent(taskSlots[0].subtasks[0].description.replace('<p>', '').replace('</p>', ''))
    }
  }, [taskSlots, tasks.length, readyTasks])

  useEffect(() => {
    scrollToBottom()
  }, [chatMessages])
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setTasks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const getCompletionMessage = (messageContent: string): string => {
    const lowerContent = messageContent.toLowerCase()

    if (lowerContent.includes('generate') && lowerContent.includes('task')) {
      return t('engine.chat.completion.tasks_added', 'Added 3 new tasks to your game.')
    } else if (lowerContent.includes('initial goals')) {
      return t(
        'engine.chat.completion.initial_goals',
        'Showing your learning goals. You can edit them directly or by prompting.',
      )
    } else if (lowerContent.includes('updated goals')) {
      return t('engine.chat.completion.updated_goals', 'Showing your updated learning goals.')
    } else if (lowerContent.includes('goal') || lowerContent.includes('objective')) {
      return t('engine.chat.completion.refined_goals', 'Refined your learning goals.')
    } else if (lowerContent.includes('story') || lowerContent.includes('narrative')) {
      return t('engine.chat.completion.updated_story', 'Updated the game story.')
    } else if (lowerContent.includes('rephrase') || lowerContent.includes('reword')) {
      return t('engine.chat.completion.rephrased_tasks', 'Rephrased the tasks for better clarity.')
    } else if (lowerContent.includes('lens') || lowerContent.includes('perspective')) {
      return t('engine.chat.completion.adjusted_lens', 'Adjusted the game lens and reshaped the approach.')
    } else if (lowerContent.includes('improve') || lowerContent.includes('clarity')) {
      return t('engine.chat.completion.improved_clarity', 'Improved clarity and balanced difficulty across your game.')
    } else {
      return t('engine.chat.completion.updated_feedback', 'Updated your game based on your feedback.')
    }
  }

  const getProgressMessages = useCallback(
    (messageContent: string): string[] => {
      const lowerContent = messageContent.toLowerCase()

      // Set loading states based on request type
      if (lowerContent.includes('initial goals')) {
        setIsUpdatingGoals(true)
        return [
          t('engine.chat.completion.progress.initial_goals.step1', 'Studying materials...'),
          t('engine.chat.completion.progress.initial_goals.step2', 'Analyzing learning goals...'),
          t('engine.chat.completion.progress.initial_goals.step3', 'Writing the goals...'),
        ]
      } else if (lowerContent.includes('generate ') || lowerContent.includes('tasks')) {
        setTimeout(() => {
          setIsUpdatingTasks(true)
        }, 2100)
        return [
          readyTasks
            ? t('engine.chat.completion.progress.generate_tasks.step1_ready_tasks', 'Analyzing your document...')
            : t('engine.chat.completion.progress.generate_tasks.step1', 'Analyzing your learning goals...'),
          t('engine.chat.completion.progress.generate_tasks.step2', 'Parsing the content...'),
          t('engine.chat.completion.progress.generate_tasks.step3', 'Generating game tasks...'),
        ]
      } else if (!showFullGame) {
        setIsUpdatingGoals(true)
        return [
          t('engine.chat.completion.progress.partial_game.step1', 'Reading your request...'),
          t('engine.chat.completion.progress.partial_game.step2', 'Modifying learning goals...'),
          t('engine.chat.completion.progress.partial_game.step3', 'Writing the goals...'),
        ]
      } else if (lowerContent.includes('story') || lowerContent.includes('narrative')) {
        setIsUpdatingTasks(true)
        return [
          t('engine.chat.completion.progress.story.step1', 'Reading current story...'),
          t('engine.chat.completion.progress.story.step2', 'Reimagining narrative...'),
          t('engine.chat.completion.progress.story.step3', 'Polishing story...'),
        ]
      } else if (lowerContent.includes('rephrase') || lowerContent.includes('reword')) {
        setIsUpdatingTasks(true)
        return [
          t('engine.chat.completion.progress.rephrase.step1', 'Analyzing current phrasing...'),
          t('engine.chat.completion.progress.rephrase.step2', 'Exploring alternatives...'),
          t('engine.chat.completion.progress.rephrase.step3', 'Refining language...'),
        ]
      } else if (lowerContent.includes('lens') || lowerContent.includes('perspective')) {
        setIsUpdatingTasks(true)
        return [
          t('engine.chat.completion.progress.lens.step1', 'Considering lens options...'),
          t('engine.chat.completion.progress.lens.step2', 'Adjusting perspective...'),
          t('engine.chat.completion.progress.lens.step3', 'Reshaping approach...'),
        ]
      } else if (lowerContent.includes('improve') || lowerContent.includes('clarity')) {
        setIsUpdatingTasks(true)
        return [
          t('engine.chat.completion.progress.improve.step1', 'Reviewing game structure...'),
          t('engine.chat.completion.progress.improve.step2', 'Identifying improvements...'),
          t('engine.chat.completion.progress.improve.step3', 'Applying enhancements...'),
        ]
      } else {
        // Default progress messages
        setIsUpdatingTasks(true)
        return [
          t('engine.chat.completion.progress.default.step1', 'Reading your request...'),
          t('engine.chat.completion.progress.default.step2', 'Analyzing changes...'),
          t('engine.chat.completion.progress.default.step3', 'Updating game...'),
        ]
      }
    },
    [showFullGame, readyTasks],
  )

  const showProgressMessages = useCallback(
    async (userMessage: string, slow?: boolean) => {
      const progressMessages = getProgressMessages(userMessage)
      for (let i = 0; i < progressMessages.length; i++) {
        if (!slow) {
          await new Promise((resolve) => setTimeout(resolve, i === 0 ? 300 : 800))
        } else {
          await new Promise((resolve) => setTimeout(resolve, i === 0 ? 1000 : 5000))
        }
        const systemMessage: ChatMessage = {
          id: `system-${Date.now()}-${i}`,
          role: 'system',
          content: progressMessages[i],
        }
        setChatMessages((prev) => [...prev, systemMessage])
      }
    },
    [getProgressMessages],
  )

  //GOALS UPDATE RECEIVED HANDLING
  useEffect(() => {
    const run = async () => {
      if (learningGoals && learningGoals.length > 0 && !showFullGame) {
        console.log('WE HAVE NEW GOALS NOW---')
        if (isUpdatingGoals) setIsUpdatingGoals(false)
        let aiResponse: ChatMessage | null = null
        if (!initialGoalsSDone.current && !isPolling) {
          initialGoalsSDone.current = true
          aiResponse = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: getCompletionMessage('initial goals'),
          }
        } else if (!isPolling) {
          aiResponse = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: getCompletionMessage('updated goals'),
          }
        }
        if (aiResponse !== null) {
          setChatMessages((prev) => prev.filter((msg) => msg.role !== 'system'))
          setChatMessages((prev) => {
            if (prev && aiResponse !== null)
              return [...prev, aiResponse]
            else return [] 
        })
          setTimeout(() => {
            setChatMessages((prev) => prev.filter((msg) => msg.role !== 'system'))
          }, 5000)
        }
        setGoals(
          learningGoals
            .map((goal) => ({
              id: goal.id,
              title: goal.title,
              description: goal.text.replace(/\[.*?\]/g, '').replace(/【.*?】/g, ''),
            }))
            .sort((a, b) => a.id - b.id),
        )
      } else {
        if (!initialGoalsStarted.current) {
          initialGoalsStarted.current = true
          await showProgressMessages(readyTasks ? 'generate tasks' : 'initial goals', true)
        }
      }
    }
    run()
  }, [learningGoals, showProgressMessages, showFullGame, readyTasks, isPolling, isUpdatingGoals])

  const startPolling = useCallback(() => {
    setTimeout(() => {
      setShouldPoll(true)
    }, 2000)
    setTimeout(() => {
      willStartPolling.current = false
    }, 2100)
  }, [setShouldPoll])

  const handleSendMessage = useCallback(
    async (message?: string, displayText?: string, slow?: boolean, hidden?: boolean) => {
      if (!chatMessage.trim() && !message) return
      let userMessageContent: string
      if (showFullGame || message) {
        //We are in task prompting phase
        userMessageContent = displayText || message || chatMessage
        onUserPromt(message || chatMessage)
      } else {
        //We are in learning goal prompting phase. Need to send a specially formatted message to BE
        const messageToSend = 'GOAL_PROMPT' + JSON.stringify(learningGoals) + '--__--' + chatMessage
        userMessageContent = chatMessage
        onUserPromt(messageToSend)
      }

      if (!hidden) {
        const newMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'user',
          content: userMessageContent,
        }
        setChatMessages((prev) => [...prev, newMessage])
      }
      setChatMessage('')

      willStartPolling.current = true
      startPolling()

      // Show context-aware progress messages
      if (!hidden) {
        await showProgressMessages(message || userMessageContent, slow)
      }
    },
    [chatMessage, learningGoals, onUserPromt, showFullGame, showProgressMessages, startPolling],
  )

  const handleQuickAction = (action: string, prompt: string) => {
    if (action === 'Choose a lens') {
      onBackToLenses()
    } else {
      setChatMessage(prompt)
    }
  }
  const handleRemoveGoal = (id: number) => {
    onRemoveLearningGoal(id)
  }
  const handleRefineGoal = (id: number, title: string, description: string) => {
    onUpdateLearningGoal({ id: id, title: title, text: description })
  }
  const handleAddGoal = () => {
    if (goals.length >= 5) {
      return // Max 5 goals
    }
    const newGoal: LearningGoal = {
      id: Date.now(),
      title: t('engine.new_goal.title', 'New Learning Goal'),
      description: t('engine.new_goal.description', 'Describe what players will learn or achieve...'),
    }
    onAddLearningGoal({ id: newGoal.id, title: newGoal.title, text: newGoal.description })
  }

  const handleContinueToFullGame = () => {
    setTimeout(() => {
      setIsUpdatingTasks(true)
    }, 2100)
    setGoalsExpanded(false)
    onDoneWithGoals()
    setShowFullGame(true)
    handleSendMessage(
      READY_PROMPTS['GET_TASKS'].prompt +
        learningGoals
          .map((goal) => {
            return goal.title + ': ' + goal.text + ' '
          })
          .join('\n'),
      t(
        'engine.display_getting_tasks_with_goals',
        'Please generate the tasks with the focus on the current learning goals.',
      ),
      true,
    )
  }

  //TASKS UPDATE RECEIVED HANDLING
  useEffect(() => {
    if (isUpdatingTasks && isPolling && tasks.length > 0) {
      setChatMessages((prev) => prev.filter((msg) => msg.role !== 'system'))
      const aiMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'system',
        content: t('engine.chat.working_with_tasks', 'Working with the tasks... (%{count})', { count: tasks.length }),
        noEffect: true,
      }
      setChatMessages((prev) => [...prev, aiMessage])
    }
    if (!isPolling && !willStartPolling.current && isUpdatingTasks) {
      setIsUpdatingTasks(false)
      //CHECK IF AUTOMATIC REVISION PROMPT HAS BEEN SENT AND SEND IF NOT
      if (!revisionPromptDone.current && !readyTasks) {
        console.log('doing the auto revision prompt now')
        setTimeout(() => {
          const REVISION_PROMPT = `Please check MISSINGWORD and ORDERITEMS tasks for three things:
          1. Chack that they include the educational introductory part as you were requested to include in them.
              The educational introductory part shoule be a short, engaging introduction that:
              •	Frames the topic in a workplace or professional context
              •	Explains why the topic is relevant in real work situations
              •	Helps players connect the task to everyday decisions, collaboration, or reflection
              •	Motivates engagement without sounding academic or school-like
              The introduction must:
              •	Align closely with the chosen theme
              •	Be clear, concise, and purposeful
              Task Quality
              •	Ensure the introduction and task address the same underlying insight or competency.
              •	Use only provided information or generally accepted workplace knowledge.
              •	Design tasks that promote thinking or application, not rote memorization.
              •	Use natural, professional, and respectful language suitable for adult learners.
          2. For MISSINGWORD tasks check that the correct options are in correct grammatical form in the language they are.
              To do this: 
              a) Temporarily create the full sentences with missing word slots so that you fill the correct options to the slots.
              b) Read the sentences and fix the grammatical form if needed.
              c) While reading the sentences also check if there is overlapping with the correct option and text outside the slot and remove the overlapping part.
                 For example if the correct option is "blue color", but the word "color" also appears directly in plain text, then remove "color" from correct option.
              d) Re-create the options array with the corrected grammatical forms of the words.
          3. For ORDERITEMS tasks, ensure that the slots are not in the numerical order that corresponds to the correct choices. The order should be random.`
          const REVISION_DISPLAY_TEST = 'Please double check the missing word tasks you provided'
          handleSendMessage(REVISION_PROMPT, REVISION_DISPLAY_TEST, false, true)
          setIsUpdatingTasks(true)
          revisionPromptDone.current = true
        }, 1000)
      } else {
        revisionPromptDone.current = true
        console.log('revision done and initial tasks done')
        setChatMessages((prev) => prev.filter((msg) => msg.role !== 'system'))
        const aiMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: !initialTasksDone.current
            ? readyTasks
              ? t(
                  'engine.chat.initial_tasks_done_ready_tasks',
                  "I've parsed the tasks from the document for your game. Review them and continue to create your game board. You can use the quick actions or free prompting to request more tasks or changes to the tasks.",
                )
              : t(
                  'engine.chat.initial_tasks_done',
                  "Nice goals! I've crafted a story and tasks for your game. Review them and continue to create your game board. You can use the quick actions or free prompting to request more tasks or changes to the tasks.",
                )
            : t('engine.chat.tasks_update_done', 'Tasks updated based on your request.'),
          subContent: initialTasksDone.current ? latestSubContent : undefined,
        }
        initialTasksDone.current = true
        setChatMessages((prev) => [...prev, aiMessage])
      }
      setLatestSubContent(undefined)
    }
  }, [isPolling, isUpdatingTasks, tasks, readyTasks, latestSubContent, setLatestSubContent,handleSendMessage])

  const handleChangeLense = useCallback(
    (values: Partial<EngineForm>) => {
      console.log(values)
      setShowLenseDialog(false)
      if (values.lense) {
        handleSendMessage(
          values.lense,
          t('engine.chat.lense_changed_visible_prompt', 'Please revise the content based on the selected Lense'),
        )
      }
      const el = document.getElementById('GOALS')
      el?.scrollIntoView()
    },
    [handleSendMessage],
  )

  return (
    <div className='min-h-screen w-full bg-gradient-to-br from-background via-background to-muted/10'>
      {/* Credit Counter Header */}
      <div className='sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border p-3 flex justify-end'>
        <CreditCounter />
      </div>
      {showLenseDialog && (
        <Modal lovable>
          <div style={{ backgroundColor: 'white', height: '90%', overflow: 'hidden', borderRadius: '30px' }}>
            <LenseSelection
              onSubmit={handleChangeLense}
              onCancel={() => {
                setShowLenseDialog(false)
              }}
            />
          </div>
        </Modal>
      )}

      <div className='container max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8 lg:pr-6 xl:pr-[500px] animate-fade-in'>
        {/* Header with Continue Button */}
        <div className='mb-6 lg:mb-8 relative'>
          <div className='text-center'>
            <div className='flex items-center justify-center gap-2 mb-2'>
              <Sparkles className='w-5 h-5 lg:w-6 lg:h-6 text-primary' />
              <Badge variant='secondary' className='text-xs'>
                {t('engine.goals_tasks.draft_mode', 'Draft Mode')}
              </Badge>
            </div>
            <h1 className='text-3xl lg:text-4xl font-bold text-foreground mb-2'>
              {t('engine.goals_tasks.title', 'Shape Your Game')}
            </h1>
            <p className='text-base lg:text-lg text-muted-foreground'>
              {t('engine.goals_tasks.sub_title', "Here's your game plan — explore, edit, and make it yours")}
            </p>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-4 lg:gap-6'>
          {/* Main Content - Left Side */}
          <div className='space-y-4 lg:space-y-6'>
            {/* Learning Goals Section */}
            {!readyTasks && (
              <Card className='p-4 lg:p-6'>
                <div className='flex items-center justify-between mb-4'>
                  <div className='flex items-center gap-3'>
                    <div className='w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center'>
                      <Target className='w-5 h-5 text-primary' />
                    </div>
                    <div id='GOALS'>
                      <h2 className='text-xl font-bold text-foreground'>
                        {t('engine.goals_tasks.goals_title', 'Learning Goals')}
                      </h2>
                      <p className='text-sm text-muted-foreground'>
                        {t('engine.goals_tasks.goals_subtitle', '%{amount} objectives', { amount: goals.length })}
                      </p>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    {goals.length < 5 && initialGoalsSDone.current && (
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={handleAddGoal}
                        className='text-primary hover:text-primary'
                      >
                        <PlusCircle className='w-4 h-4 mr-1' />
                        {t('engine.goals_tasks.add_goal', 'Add Goal')}
                      </Button>
                    )}
                    <Button variant='ghost' size='sm' onClick={() => setGoalsExpanded(!goalsExpanded)}>
                      {goalsExpanded ? <ChevronUp className='w-5 h-5' /> : <ChevronDown className='w-5 h-5' />}
                    </Button>
                  </div>
                </div>

                {goalsExpanded &&
                  (isUpdatingGoals ? (
                    <div className='space-y-3 animate-fade-in'>
                      {[1, 2, 3].map((i) => (
                        <div key={i} className='p-4 rounded-lg border border-border bg-muted/20'>
                          <Skeleton className='h-5 w-3/4 mb-3' />
                          <Skeleton className='h-4 w-full mb-2' />
                          <Skeleton className='h-4 w-5/6' />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='space-y-3 animate-fade-in'>
                      {goals.map((goal, index) => (
                        <GoalCard
                          key={goal.id}
                          id={goal.id}
                          index={index}
                          title={goal.title}
                          description={goal.description}
                          onRemove={() => handleRemoveGoal(goal.id)}
                          onRefine={(title, description) => handleRefineGoal(goal.id, title, description)}
                        />
                      ))}
                    </div>
                  ))}

                {/* Continue Button - Only show if full game is not revealed yet */}
                {!showFullGame && goalsExpanded && !isUpdatingGoals && initialGoalsSDone.current && (
                  <div className='mt-6 flex justify-center'>
                    <Button
                      onClick={handleContinueToFullGame}
                      size='lg'
                      className='bg-gradient-to-r from-primary to-primary-glow hover:opacity-90 transition-opacity shadow-lg px-8'
                    >
                      {t('engine.goals_tasks.continue_to_tasks', 'Continue to Draft Story & Tasks')}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* Game Story Section - Only show after continue */}
            {showFullGame && !storyRemoved && (
              <Card className='p-4 lg:p-6 group'>
                <div className='flex items-center justify-between mb-4'>
                  <div className='flex items-center gap-3'>
                    <div className='w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center'>
                      <BookOpen className='w-5 h-5 text-accent' />
                    </div>
                    <div>
                      <h2 className='text-xl font-bold text-foreground'>
                        {t('engine.goals_tasks.story_title', 'Game Story')}
                      </h2>
                      <p className='text-sm text-muted-foreground'>
                        {t('engine.goals_tasks.story_subtitle', 'Narrative context')}
                      </p>
                    </div>
                  </div>
                  {
                    <div className='flex items-center gap-2'>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => setStoryRemoved(true)}
                        className='text-destructive hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity'
                      >
                        {t('engine.remove', 'Remove')}
                      </Button>
                    </div>
                  }
                </div>

                {isUpdatingStory ? (
                  <div className='p-4 bg-muted/30 rounded-lg border border-border'>
                    <Skeleton className='h-4 w-full mb-2' />
                    <Skeleton className='h-4 w-5/6 mb-2' />
                    <Skeleton className='h-4 w-4/5' />
                  </div>
                ) : (
                  <div className='p-4 bg-muted/30 rounded-lg border border-border'>
                    <p className='text-sm text-muted-foreground leading-relaxed line-clamp-2'>
                      {gameStoryContent ?? t('engine.story_placeholder', 'Generating story...')}
                    </p>
                    {/*<button
                      onClick={() => setStoryExpanded(true)}
                      className='mt-2 text-xs text-primary hover:text-primary-glow font-medium transition-colors'
                    >
                      Read more
                    </button>*/}
                  </div>
                )}
              </Card>
            )}

            {/* Game Tasks Section - Only show after continue */}
            {showFullGame && (
              <Card className='p-4 lg:p-6'>
                <div className='flex items-center justify-between mb-4'>
                  <div className='flex items-center gap-3'>
                    <div className='w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center'>
                      <CheckSquare className='w-5 h-5 text-secondary' />
                    </div>
                    <div>
                      <h2 className='text-xl font-bold text-foreground'>
                        {t('engine.goals_tasks.tasks_title', 'Game Tasks')}
                      </h2>
                      <p className='text-sm text-muted-foreground'>
                        {t(
                          'engine.goals_tasks.tasks_subtitle',
                          '%{tasks} challenges • %{points} points • ~ %{minutes} min play time',
                          {
                            tasks: tasks.length,
                            points: tasks.reduce((sum, task) => sum + (task.pointsSum ?? 0), 0).toLocaleString(),
                            minutes: tasks.length * 2.5,
                          },
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      setTasksCollapsed(!tasksCollapsed)
                      document
                        .querySelectorAll('[class*="TaskPreview_toggleCollapseButton"]')
                        .forEach((el) => (el as HTMLButtonElement).click())
                    }}
                    className='text-muted-foreground hover:text-foreground'
                  >
                    {tasksCollapsed
                      ? t('engine.goals_tasks.expand_all', 'Expand All')
                      : t('engine.goals_tasks.collapse_all', 'Collapse All')}
                  </Button>
                </div>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                    <div className='space-y-3'>
                      {isUpdatingTasks && tasks.length < 1
                        ? [1, 2, 3].map((i) => (
                            <div key={i} className='p-4 rounded-lg border border-border bg-card animate-fade-in'>
                              <div className='flex items-center justify-between mb-3'>
                                <Skeleton className='h-5 w-1/3' />
                                <Skeleton className='h-6 w-20' />
                              </div>
                              <Skeleton className='h-4 w-full mb-2' />
                              <Skeleton className='h-4 w-4/5' />
                            </div>
                          ))
                        : tasks.filter((t) => initialTasksDone.current || t.type !== ExerciseType.MissingWordExercise).map((task, index) => (
                            <TaskPreview
                              key={`task_${task.order}`}
                              task={{ ...task, name: task.order + '. ' + task.name }}
                              noPointsGame={false}
                              initialExpanded
                              wide
                            />
                          ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </Card>
            )}

            {/* Navigation Buttons - Only show after full game is revealed */}
            {showFullGame ? (
              <div className='mt-4 lg:mt-6 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center'>
                {!readyTasks && (
                  <Button variant='outline' onClick={() => setShowLenseDialog(true)} className='w-full sm:w-auto'>
                    {t('engine.goals_tasks.show_lenses_button', 'Show Lense Selection')}
                  </Button>
                )}
                <Button variant='outline' onClick={() => onCancel()} className='w-full sm:w-auto'>
                  {t('engine.goals_tasks.cancel_button', 'Cancel')}
                </Button>
                <Button
                  onClick={onContinue}
                  size='lg'
                  className='w-full sm:w-auto shadow-lg bg-gradient-to-r from-primary to-primary-glow hover:shadow-xl transition-shadow'
                >
                  {is3D ? t('engine.board.proceed', 'Proceed to Publish') : t('engine.goals_tasks.continue_to_board_button', 'Continue to Game Board')}
                </Button>
              </div>
            ) : (
              <div className='mt-4 lg:mt-6 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center'>
                <Button variant='outline' onClick={() => onCancel()} className='w-full sm:w-auto'>
                  {t('engine.goals_tasks.cancel_button', 'Cancel')}
                </Button>
              </div>
            )}
          </div>

          {/* AI Chat Partner - Fixed Right Side */}
          <div className='hidden xl:block fixed right-4 xl:right-6 top-28 bottom-6 w-[420px] xl:w-[460px] z-20'>
            <Card className='p-4 xl:p-6 shadow-lg bg-gradient-to-br from-primary/5 via-background to-accent/5 border-primary/20 h-full flex flex-col'>
              {/* Header with Avatar */}
              <div className='flex items-center gap-3 mb-4 xl:mb-6 pb-3 xl:pb-4 border-b border-primary/20 flex-shrink-0'>
                <div className='w-10 h-10 xl:w-12 xl:h-12 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-md'>
                  <Sparkles className='w-5 h-5 xl:w-6 xl:h-6 text-primary-foreground' />
                </div>
                <div className='flex-1'>
                  <h2 className='text-lg xl:text-xl font-bold text-foreground'>{t('engine.chat_header','Saige')}</h2>
                  <p className='text-xs xl:text-sm text-primary/70 font-medium'>{t('engine.chat_subheader','Your AI game designer')}</p>
                </div>
              </div>

              {/* Chat Messages Area */}
              <div className='mb-4 xl:mb-6 flex-1 overflow-hidden relative min-h-0'>
                {/* Top fade indicator */}
                <div className='absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-background/80 via-background/40 to-transparent pointer-events-none z-10' />

                <div className='space-y-4 h-full overflow-y-auto pr-2 py-2'>
                  {chatMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex gap-3 ${msg.noEffect ? '' : 'animate-fade-in'} ${
                        msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'
                      }`}
                    >
                      {msg.role === 'assistant' && (
                        <div className='w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shrink-0 border border-primary/20'>
                          <Sparkles className='w-4 h-4 text-primary' />
                        </div>
                      )}
                      {msg.role === 'system' ? (
                        <div className='flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs italic font-medium'>
                          <div className='w-1.5 h-1.5 rounded-full bg-primary animate-pulse' />
                          <span>{msg.content}</span>
                        </div>
                      ) : (
                        <div
                          className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed ${
                            msg.role === 'user'
                              ? 'bg-gradient-to-br from-primary to-primary-glow text-primary-foreground shadow-md'
                              : 'bg-card text-foreground border border-primary/10 shadow-sm'
                          }`}
                        >
                          {msg.content}
                          {msg.subContent && (
                            <>
                              {subContentsExpanded.includes(msg.id) ? (
                                <div
                                  style={{ textAlign: 'center' }}
                                  onClick={() => setSubContentsExpanded((prev) => prev.filter((id) => id !== msg.id))}
                                >
                                  <ChevronUp className='w-5 h-5' />
                                </div>
                              ) : (
                                <div
                                  style={{ textAlign: 'center' }}
                                  onClick={() => setSubContentsExpanded((prev) => prev.concat(msg.id))}
                                >
                                  <ChevronDown className='w-5 h-5' />
                                </div>
                              )}
                              {subContentsExpanded.includes(msg.id) && <div>{msg.subContent}</div>}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={chatMessagesEndRef} />
                </div>

                {/* Bottom fade indicator */}
                <div className='absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background/80 via-background/40 to-transparent pointer-events-none z-10' />
              </div>
              {/*autoPrompt && (
                <div className='mb-3 flex-shrink-0'>
                  {
                    "It appears that some tasks can't be parsed to Seppo tasks currently, Would you like me to revise those tasks?"
                  }
                  <Button
                    onClick={() =>
                      handleSendMessage(
                        READY_PROMPTS.CHECK_SPECIFIC_TASK_TYPES.prompt.replace('REPLACE_THIS', autoPrompt),
                        READY_PROMPTS.CHECK_SPECIFIC_TASK_TYPES.displayText,
                      )
                    }
                    className='w-full shadow-md hover:shadow-lg transition-shadow bg-gradient-to-r from-primary to-primary-glow'
                  >
                    <Send className='w-4 h-4 mr-2' />
                    {READY_PROMPTS.CHECK_SPECIFIC_TASK_TYPES.title}
                  </Button>
                </div>
              )*/}

              {showFullGame && (
                <div className='mb-3 flex-shrink-0'>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        size='sm'
                        className='w-full justify-center gap-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary/10 transition-colors text-primary'
                      >
                        <Plus className='w-4 h-4' />
                        <span className='text-sm font-medium'>{t('engine.quick_actions','Quick Actions')}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-80 p-4' align='center'>
                      <QuickActionsBar
                        onAction={handleQuickAction}
                        readyTasks={readyTasks}
                        hasLenseSelected={hasLenseSelected}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Chat Input */}
              <div className='space-y-2 xl:space-y-3 flex-shrink-0'>
                <Textarea
                  placeholder={t('engine.prompt_placeholder', 'Ask Saige')}
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  className='min-h-[80px] xl:min-h-[90px] resize-none border-primary/20 focus:border-primary transition-colors bg-card text-sm'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                />
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!chatMessage.trim()}
                  className='w-full shadow-md hover:shadow-lg transition-shadow bg-gradient-to-r from-primary to-primary-glow'
                >
                  <Send className='w-4 h-4 mr-2' />
                  {t('engine.prompt_message_button','Send Message')}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
